import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../lib/api";
import { toast } from "sonner";
import { fmtDate } from "../lib/utils-app";
import { Lock, FileText, Download, Package, Shield, Eye, X } from "lucide-react";

const isPreviewable = (d) => /pdf|^image\//.test(d.content_type || "") || /\.(pdf|png|jpe?g|webp)$/i.test(d.original_filename || "");

export default function PublicShare() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [password, setPassword] = useState("");
  const [payload, setPayload] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/share/${token}/meta`);
        setMeta(data);
        if (!data.has_password) {
          await unlock("");
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || "Link non disponibile");
      }
    })();
    // eslint-disable-next-line
  }, [token]);

  const unlock = async (pw) => {
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API_BASE}/public/share/${token}/access`, { password: pw });
      setPayload(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const download = (docId, filename) => {
    const url = `${API_BASE}/public/share/${token}/download/${docId}?password=${encodeURIComponent(password)}`;
    window.open(url, "_blank");
  };

  const downloadAll = () => {
    const url = `${API_BASE}/public/share/${token}/download-all?password=${encodeURIComponent(password)}`;
    window.open(url, "_blank");
  };

  if (err && !meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6">
        <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-md text-center">
          <Shield size={40} className="mx-auto text-red-500 mb-3" />
          <h1 className="font-display text-xl font-bold text-slate-900">Link non disponibile</h1>
          <p className="text-slate-500 mt-2">{err}</p>
        </div>
      </div>
    );
  }
  if (!meta) return <div className="min-h-screen flex items-center justify-center text-slate-500">Caricamento…</div>;

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center gap-3">
          <img src="/logo-studio-aragona.png" alt="Studio Aragona" className="w-11 h-11 rounded bg-white p-1 object-contain" />
          <div>
            <div className="font-display font-bold text-lg">Studio Aragona</div>
            <div className="text-xs text-slate-400">Condivisione documenti sicurezza</div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
          <div className="text-xs font-mono uppercase tracking-widest text-blue-700 mb-1">Documenti condivisi</div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{meta.nome}</h1>
          {meta.note && <p className="text-slate-600 mt-2">{meta.note}</p>}
          <div className="text-xs text-slate-500 mt-3">
            {meta.expires_at && <>Link valido fino al <span className="font-medium">{fmtDate(meta.expires_at)}</span></>}
          </div>
        </div>

        {!payload && meta.has_password && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-md">
            <div className="flex items-center gap-2 mb-3">
              <Lock size={18} className="text-amber-600" />
              <h2 className="font-display font-semibold">Password richiesta</h2>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); unlock(password); }} className="space-y-3">
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="public-password"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="Password" />
              {err && <div className="text-sm text-red-600">{err}</div>}
              <button type="submit" disabled={busy} data-testid="public-unlock"
                className="w-full bg-slate-900 text-white py-2 rounded-lg disabled:opacity-60">
                {busy ? "Verifica…" : "Sblocca"}
              </button>
            </form>
          </div>
        )}

        {payload && (
          <>
            <div className="flex justify-end mb-3">
              <button onClick={downloadAll} data-testid="download-all"
                className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
                <Package size={16} /> Scarica tutti (.zip)
              </button>
            </div>
            {(() => {
              const groups = [];
              const byKey = {};
              payload.documents.forEach(d => {
                const key = `${d.entity_type || "company"}|${d.entity_id}`;
                if (!byKey[key]) { byKey[key] = { key, type: d.entity_type || "company", name: d.entity_name || d.company_name, docs: [] }; groups.push(byKey[key]); }
                byKey[key].docs.push(d);
              });
              const label = { company: "Documenti Azienda", worker: "Documenti Lavoratore", site: "Documenti Cantiere", equipment: "Documenti Attrezzatura" };
              return groups.map(g => (
                <section key={g.key} className="mb-5" data-testid={`public-group-${g.type}`}>
                  <h2 className="text-xs font-mono uppercase tracking-widest text-blue-700 mb-2">{label[g.type]} {g.type !== "company" && <span className="text-slate-700 normal-case tracking-normal font-sans font-semibold">— {g.name}</span>} <span className="text-slate-400">({g.docs.length})</span></h2>
                  <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {g.docs.map(d => (
                <div key={d.id} className="flex items-center gap-4 p-4 hover:bg-slate-50">
                  <div className="w-10 h-10 rounded bg-blue-50 text-blue-800 flex items-center justify-center shrink-0"><FileText size={18} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{d.nome}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{d.categoria} · <span className="font-mono">{d.original_filename}</span></div>
                    {d.data_scadenza && <div className="text-xs text-slate-500 mt-0.5">Scadenza: {fmtDate(d.data_scadenza)}</div>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {isPreviewable(d) && (
                      <button onClick={() => setPreview(d)} data-testid={`preview-${d.id}`}
                        className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-100 text-slate-700 px-3 py-1.5 rounded-lg text-sm">
                        <Eye size={14} /> Anteprima
                      </button>
                    )}
                    <button onClick={() => download(d.id, d.original_filename)}
                      data-testid={`download-${d.id}`}
                      className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-100 text-slate-700 px-3 py-1.5 rounded-lg text-sm">
                      <Download size={14} /> Scarica
                    </button>
                  </div>
                </div>
              ))}
                  </div>
                </section>
              ));
            })()}
            {payload.documents.length === 0 && <div className="p-6 text-center text-slate-500 bg-white border border-slate-200 rounded-lg">Nessun documento disponibile.</div>}
          </>
        )}
      </main>

      {preview && (() => {
        const src = `${API_BASE}/public/share/${token}/preview/${preview.id}?password=${encodeURIComponent(password)}`;
        const isImg = /^image\//.test(preview.content_type || "") || /\.(png|jpe?g|webp)$/i.test(preview.original_filename || "");
        return (
          <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreview(null)}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="public-preview-dialog">
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
                <div className="min-w-0">
                  <div className="font-display font-semibold truncate">{preview.nome}</div>
                  <div className="text-xs text-slate-500 font-mono truncate">{preview.categoria} · {preview.original_filename}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => download(preview.id, preview.original_filename)} className="inline-flex items-center gap-1 text-sm text-blue-700 hover:bg-blue-50 px-3 py-1.5 rounded-lg"><Download size={15} /> Scarica</button>
                  <button onClick={() => setPreview(null)} data-testid="public-preview-close" className="p-1.5"><X size={20} /></button>
                </div>
              </div>
              <div className="flex-1 bg-slate-100 overflow-auto flex items-center justify-center">
                {isImg ? <img src={src} alt={preview.nome} className="max-w-full max-h-full object-contain" data-testid="public-preview-image" />
                  : <iframe title="Anteprima" src={src} className="w-full h-full" data-testid="public-preview-pdf" />}
              </div>
            </div>
          </div>
        );
      })()}

      <footer className="max-w-5xl mx-auto px-4 sm:px-6 py-8 text-xs text-slate-500 text-center">
        © {new Date().getFullYear()} Studio Aragona · Piattaforma sicurezza sul lavoro
      </footer>
    </div>
  );
}
